import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface BlackforestlabsFlux2ProEditParams {
  /**
   * 编辑指令的文本提示词
   * @example "将背景改为蓝天白云，保持主体不变"
   */
  prompt?: string;
  /**
   * 必需：要编辑的图像URL或Base64编码
   * @example "https://example.com/image.jpg"
   */
  input_image: string;
  /**
   * 是否启用提示词优化增强
   * @default true
   */
  prompt_upsampling?: boolean;
  /**
   * 输出图像宽度（像素），0表示自动
   * @default 0
   * @range 64 - ∞
   */
  width?: number;
  /**
   * 输出图像高度（像素），0表示自动
   * @default 0
   * @range 64 - ∞
   */
  height?: number;
  /**
   * 内容安全容忍度（0-5），0最严格，5最宽松
   * @default 2
   * @range 0 - 5
   */
  safety_tolerance?: number;
  /**
   * 输出图像格式
   * @default "jpeg"
   * @values jpeg | png
   */
  output_format?: 'jpeg' | 'png';
  /**
   * 第2张参考图像URL或Base64编码（可选）
   */
  input_image_2?: string;
  /**
   * 第3张参考图像URL或Base64编码（可选）
   */
  input_image_3?: string;
  /**
   * 第4张参考图像URL或Base64编码（可选）
   */
  input_image_4?: string;
  /**
   * 第5张参考图像URL或Base64编码（实验性）
   */
  input_image_5?: string;
  /**
   * 第6张参考图像URL或Base64编码（实验性）
   */
  input_image_6?: string;
  /**
   * 第7张参考图像URL或Base64编码（实验性）
   */
  input_image_7?: string;
  /**
   * 第8张参考图像URL或Base64编码（实验性）
   */
  input_image_8?: string;
  /**
   * 随机种子，用于结果可复现
   * @example 42
   */
  seed?: number;
  /**
   * 接收Webhook通知的URL
   */
  webhook_url?: string;
  /**
   * Webhook签名验证密钥
   */
  webhook_secret?: string;
}

export interface BlackforestlabsFlux2ProEditResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * blackforestlabs_flux_2_pro_edit
 *
 * <p>使用FLUX.2 [PRO]模型编辑现有图像，必须提供input_image参数</p>
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, blackforestlabsFlux2ProEdit } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await blackforestlabsFlux2ProEdit({
 *   // 参数见 BlackforestlabsFlux2ProEditParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 BlackforestlabsFlux2ProEditParams 接口定义
 * @returns 生成的资源列表
 */
export async function blackforestlabsFlux2ProEdit(
  params: BlackforestlabsFlux2ProEditParams,
): Promise<BlackforestlabsFlux2ProEditResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'blackforestlabs_flux_2_pro_edit',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: BlackforestlabsFlux2ProEditResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
